<template>
  <div
    class="home-view bg-gradient-to-br from-[#f7f2e0]/5 via-white to-[#f7f2e0]/10 text-[#212924] snap-y snap-mandatory scroll-smooth overflow-y-auto min-h-screen"
  >
    <!-- Hero Section Modernisée -->
    <section
      class="relative min-h-[91vh] flex items-center justify-center overflow-hidden snap-start"
    >
      <!-- Background avec effet parallax -->
      <div class="absolute inset-0 z-0">
        <div
          class="absolute inset-0 bg-gradient-to-br from-[#212924]/40 via-[#a84925]/30 to-[#cb9836]/20"
        ></div>
        <img
          src="@assets/images/presentation/Vue_1.jpg"
          alt="Vue de Pompéi"
          class="w-full h-full object-cover scale-105 transform motion-safe:animate-ken-burns"
        />
        <!-- Overlay moderne avec mesh gradient -->
        <div
          class="absolute inset-0 bg-gradient-to-tr from-[#212924]/60 via-transparent to-[#cb9836]/30"
        ></div>
        <div
          class="absolute inset-0 bg-gradient-to-bl from-transparent via-[#a84925]/20 to-[#212924]/50"
        ></div>
      </div>

      <!-- Floating elements décoratifs -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute top-1/4 left-1/4 w-32 h-32 bg-[#cb9836]/10 rounded-full blur-3xl motion-safe:animate-float"
        ></div>
        <div
          class="absolute top-3/4 right-1/4 w-24 h-24 bg-[#a84925]/15 rounded-full blur-2xl motion-safe:animate-float-delayed"
        ></div>
        <div
          class="absolute bottom-1/4 left-1/3 w-40 h-40 bg-white/5 rounded-full blur-3xl motion-safe:animate-float-slow"
        ></div>
      </div>

      <!-- Hero Content avec glassmorphism -->
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          class="text-center backdrop-blur-sm bg-white/5 rounded-3xl border border-white/20 p-8 md:p-12 shadow-2xl max-w-5xl mx-auto"
        >
          <!-- Badge supérieur -->
          <div
            class="inline-flex items-center px-4 py-2 rounded-full bg-[#cb9836]/20 backdrop-blur-md border border-[#cb9836]/30 text-[#cb9836] text-sm font-medium mb-8 motion-safe:animate-fade-in-up"
          >
            <span class="w-2 h-2 bg-[#cb9836] rounded-full mr-2 motion-safe:animate-pulse"></span>
            {{ $t('home.hero.badge') }}
          </div>

          <h1
            class="text-5xl md:text-6xl lg:text-7xl font-extralight text-white tracking-tight mb-6 motion-safe:animate-fade-in-up motion-safe:animation-delay-200"
          >
            <span
              class="bg-gradient-to-r from-white via-white/90 to-white/80 bg-clip-text text-transparent"
            >
              {{ $t('home.hero.title.part1') }}
            </span>
            {{ ' ' }}
            <span
              class="bg-gradient-to-r from-[#cb9836] via-[#f7f2e0] to-[#cb8f36] bg-clip-text text-transparent font-light"
            >
              {{ $t('home.hero.title.part2') }}
            </span>
          </h1>

          <div
            class="space-y-4 mb-12 motion-safe:animate-fade-in-up motion-safe:animation-delay-400"
          >
            <p
              class="text-xl md:text-2xl text-white/95 font-light leading-relaxed max-w-3xl mx-auto"
            >
              {{ $t('home.hero.subtitle') }}
            </p>
            <p class="text-lg text-white/80 font-light leading-relaxed max-w-2xl mx-auto">
              {{ $t('home.hero.description') }}
            </p>
          </div>

          <!-- CTA Buttons Modernisés -->
          <div
            class="flex flex-col sm:flex-row justify-center gap-4 motion-safe:animate-fade-in-up motion-safe:animation-delay-600"
          >
            <RouterLink
              to="/browse"
              class="group relative px-8 py-4 bg-white/20 backdrop-blur-md rounded-2xl text-white border border-white/30 hover:bg-white/30 hover:border-white/50 transition-all duration-500 font-medium shadow-lg hover:shadow-xl hover:scale-105 overflow-hidden"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-[#cb9836]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>
              <div class="relative flex items-center justify-center">
                <Search
                  class="h-5 w-5 mr-3 group-hover:rotate-12 transition-transform duration-300"
                />
                {{ $t('home.hero.cta.explore') }}
              </div>
            </RouterLink>

            <RouterLink
              to="/viewer"
              class="group relative px-8 py-4 bg-gradient-to-r from-[#cb9836] to-[#a84925] rounded-2xl text-white hover:from-[#cb9836]/90 hover:to-[#a84925]/90 transition-all duration-500 font-medium shadow-lg hover:shadow-xl hover:scale-105 overflow-hidden"
            >
              <div
                class="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>
              <div class="relative flex items-center justify-center">
                <Eye class="h-5 w-5 mr-3 group-hover:scale-110 transition-transform duration-300" />
                {{ $t('home.hero.cta.viewer') }}
              </div>
            </RouterLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Présentation Modernisée -->
    <section
      class="min-h-[90vh] bg-gradient-to-br from-white via-[#f7f2e0]/5 to-white relative overflow-hidden snap-start flex items-center py-20"
    >
      <!-- Background pattern -->
      <div class="absolute inset-0 opacity-30">
        <div
          class="absolute inset-0 bg-gradient-to-r from-[#cb9836]/5 via-transparent to-[#a84925]/5"
        ></div>
        <div
          class="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#cb9836]/20 to-transparent"
        ></div>
      </div>

      <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div class="text-center mb-16">
          <div
            class="inline-flex items-center px-4 py-2 rounded-full bg-[#cb9836]/10 text-[#cb9836] text-sm font-medium mb-6 motion-safe:animate-fade-in-up"
          >
            <div class="w-2 h-2 bg-[#cb9836] rounded-full mr-2"></div>
            {{ $t('home.presentation.badge') }}
          </div>

          <h2
            class="text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight text-[#212924] mb-8 motion-safe:animate-fade-in-up motion-safe:animation-delay-200"
          >
            {{ $t('home.presentation.title.part1') }}
            <span
              class="bg-gradient-to-r from-[#cb9836] to-[#a84925] bg-clip-text text-transparent font-light"
            >
              {{ $t('home.presentation.title.part2') }}
            </span>
          </h2>

          <p
            class="text-xl text-[#212924]/70 max-w-4xl mx-auto font-light leading-relaxed motion-safe:animate-fade-in-up motion-safe:animation-delay-400"
          >
            {{ $t('home.presentation.description') }}
          </p>
        </div>

        <!-- Features Grid Modernisé -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 md:grid-rows-1">
          <div class="group motion-safe:animate-fade-in-up motion-safe:animation-delay-600 h-full">
            <div
              class="relative p-8 lg:p-10 rounded-3xl bg-white/80 backdrop-blur-sm border border-[#cb9836]/10 hover:border-[#cb9836]/30 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden h-full flex flex-col"
            >
              <!-- Gradient background -->
              <div
                class="absolute inset-0 bg-gradient-to-br from-[#cb9836]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>

              <div class="relative flex flex-col h-full">
                <div
                  class="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-[#cb9836]/20 to-[#cb9836]/10 text-[#cb9836] mb-8 group-hover:scale-110 transition-transform duration-300"
                >
                  <BookOpen class="h-8 w-8" />
                </div>

                <h3
                  class="text-xl font-medium text-[#212924] mb-4 group-hover:text-[#cb9836] transition-colors duration-300"
                >
                  {{ $t('home.features.documentation.title') }}
                </h3>

                <p class="text-[#212924]/70 font-light leading-relaxed text-lg flex-grow">
                  {{ $t('home.features.documentation.description') }}
                </p>
              </div>
            </div>
          </div>

          <div class="group motion-safe:animate-fade-in-up motion-safe:animation-delay-700 h-full">
            <div
              class="relative p-8 lg:p-10 rounded-3xl bg-white/80 backdrop-blur-sm border border-[#cb9836]/10 hover:border-[#cb9836]/30 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden h-full flex flex-col"
            >
              <div
                class="absolute inset-0 bg-gradient-to-br from-[#a84925]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>

              <div class="relative flex flex-col h-full">
                <div
                  class="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-[#a84925]/20 to-[#a84925]/10 text-[#a84925] mb-8 group-hover:scale-110 transition-transform duration-300"
                >
                  <Layers class="h-8 w-8" />
                </div>

                <h3
                  class="text-xl font-medium text-[#212924] mb-4 group-hover:text-[#a84925] transition-colors duration-300"
                >
                  {{ $t('home.features.visualization.title') }}
                </h3>

                <p class="text-[#212924]/70 font-light leading-relaxed text-lg flex-grow">
                  {{ $t('home.features.visualization.description') }}
                </p>
              </div>
            </div>
          </div>

          <div class="group motion-safe:animate-fade-in-up motion-safe:animation-delay-800 h-full">
            <div
              class="relative p-8 lg:p-10 rounded-3xl bg-white/80 backdrop-blur-sm border border-[#cb9836]/10 hover:border-[#cb9836]/30 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden h-full flex flex-col"
            >
              <div
                class="absolute inset-0 bg-gradient-to-br from-[#cb9836]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>

              <div class="relative flex flex-col h-full">
                <div
                  class="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-[#cb9836]/20 to-[#cb9836]/10 text-[#cb9836] mb-8 group-hover:scale-110 transition-transform duration-300"
                >
                  <Edit3 class="h-8 w-8" />
                </div>

                <h3
                  class="text-xl font-medium text-[#212924] mb-4 group-hover:text-[#cb9836] transition-colors duration-300"
                >
                  {{ $t('home.features.annotation.title') }}
                </h3>

                <p class="text-[#212924]/70 font-light leading-relaxed text-lg flex-grow">
                  {{ $t('home.features.annotation.description') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Logo Redesignée -->
    <section class="relative min-h-[80vh] overflow-hidden snap-start flex items-center py-20">
      <!-- Background moderne avec gradients -->
      <div class="absolute inset-0">
        <div
          class="absolute inset-0 bg-gradient-to-br from-[#a84925] via-[#a84925]/95 to-[#212924]"
        ></div>
        <div
          class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/5"
        ></div>
        <!-- Pattern overlay -->
        <div
          class="absolute inset-0 opacity-10 bg-gradient-to-r from-transparent via-white/10 to-transparent"
        ></div>
      </div>

      <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          <!-- Logo Section -->
          <div class="w-full lg:w-2/5 motion-safe:animate-fade-in-left">
            <div class="relative">
              <!-- Glow effect -->
              <div
                class="absolute inset-0 bg-gradient-to-r from-[#cb9836]/20 to-transparent rounded-3xl blur-3xl"
              ></div>
              <div
                class="relative p-8 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl"
              >
                <img
                  src="@assets/images/presentation/Logo_BdC.png"
                  alt="Logo Bruits de Couloir"
                  class="w-full max-w-md mx-auto drop-shadow-2xl rounded-2xl"
                />
              </div>
            </div>
          </div>

          <!-- Content Section -->
          <div
            class="w-full lg:w-3/5 text-white motion-safe:animate-fade-in-right motion-safe:animation-delay-300"
          >
            <div class="space-y-8">
              <div>
                <h2 class="text-4xl md:text-5xl font-extralight mb-6 tracking-tight">
                  {{ $t('home.about.title.part1') }}
                  <span
                    class="bg-gradient-to-r from-[#cb9836] to-[#f7f2e0] bg-clip-text text-transparent font-light"
                  >
                    {{ $t('home.about.title.part2') }}
                  </span>
                </h2>
              </div>

              <div class="space-y-6 text-lg font-light leading-relaxed text-white/90">
                <p class="relative pl-6 border-l-2 border-[#cb9836]/50">
                  {{ $t('home.about.paragraph1') }}
                </p>

                <p class="relative pl-6 border-l-2 border-[#cb9836]/30">
                  {{ $t('home.about.paragraph2') }}
                </p>

                <p class="relative pl-6 border-l-2 border-white/30">
                  {{ $t('home.about.paragraph3') }}
                </p>
              </div>

              <!-- Partners logos ou badges -->
              <div class="flex flex-wrap gap-4 pt-8">
                <div
                  class="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm font-medium"
                >
                  {{ $t('home.partners.sorbonne') }}
                </div>
                <div
                  class="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm font-medium"
                >
                  {{ $t('home.partners.uqam') }}
                </div>
                <div
                  class="px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 text-sm font-medium"
                >
                  {{ $t('home.partners.mercurio') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Actualités -->
    <section
      class="min-h-[80vh] bg-gradient-to-br from-white via-[#f7f2e0]/5 to-white relative overflow-hidden snap-start flex items-center py-20"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div class="text-center mb-12">
          <h2
            class="text-4xl md:text-5xl font-extralight tracking-tight text-[#212924] mb-4 motion-safe:animate-fade-in-up"
          >
            {{ $t('home.news.title') }}
            <span
              class="bg-gradient-to-r from-[#cb9836] to-[#a44525] bg-clip-text text-transparent font-light"
            >
              {{ $t('home.news.titleHighlight') }}
            </span>
          </h2>
        </div>

        <LatestNews />

        <div class="text-center mt-8">
          <RouterLink
            to="/news"
            class="inline-flex items-center px-6 py-3 bg-bdc-ocre text-white rounded-lg hover:bg-bdc-ocre/90 transition-colors font-medium"
          >
            {{ $t('home.news.viewAll') }}
            <ArrowRight class="w-5 h-5 ml-2" />
          </RouterLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import LatestNews from '@/components/blog/LatestNews.vue'
import { ArrowRight, BookOpen, Edit3, Eye, Layers, Search } from 'lucide-vue-next'
import { RouterLink } from 'vue-router'
</script>

<style scoped>
/* Animations modernes */
@keyframes ken-burns {
  0% {
    transform: scale(1.05) rotate(0deg);
  }
  50% {
    transform: scale(1.1) rotate(0.5deg);
  }
  100% {
    transform: scale(1.05) rotate(0deg);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

@keyframes float-delayed {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(-3deg);
  }
}

@keyframes float-slow {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in-right {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Classes d'animation */
.motion-safe\:animate-ken-burns {
  animation: ken-burns 20s ease-in-out infinite;
}

.motion-safe\:animate-float {
  animation: float 6s ease-in-out infinite;
}

.motion-safe\:animate-float-delayed {
  animation: float-delayed 8s ease-in-out infinite 2s;
}

.motion-safe\:animate-float-slow {
  animation: float-slow 10s ease-in-out infinite 4s;
}

.motion-safe\:animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

.motion-safe\:animate-fade-in-left {
  animation: fade-in-left 0.8s ease-out forwards;
  opacity: 0;
}

.motion-safe\:animate-fade-in-right {
  animation: fade-in-right 0.8s ease-out forwards;
  opacity: 0;
}

/* Délais d'animation */
.motion-safe\:animation-delay-200 {
  animation-delay: 0.2s;
}

.motion-safe\:animation-delay-300 {
  animation-delay: 0.3s;
}

.motion-safe\:animation-delay-400 {
  animation-delay: 0.4s;
}

.motion-safe\:animation-delay-600 {
  animation-delay: 0.6s;
}

.motion-safe\:animation-delay-700 {
  animation-delay: 0.7s;
}

.motion-safe\:animation-delay-800 {
  animation-delay: 0.8s;
}

/* Respect du prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
  .motion-safe\:animate-ken-burns,
  .motion-safe\:animate-float,
  .motion-safe\:animate-float-delayed,
  .motion-safe\:animate-float-slow,
  .motion-safe\:animate-fade-in-up,
  .motion-safe\:animate-fade-in-left,
  .motion-safe\:animate-fade-in-right {
    animation: none;
    opacity: 1;
    transform: none;
  }
}

/* Smooth scroll et snap adouci */
html {
  scroll-behavior: smooth;
}

.home-view {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Snap adouci - suppression des règles trop strictes */
section {
  scroll-snap-align: start;
}
</style>
